import React, { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Carousel } from '@mantine/carousel';
import { Divider, Skeleton } from '@mantine/core';
import AppStack from '../../common/AppStack';
import { Context as StoreUserContext } from '../../../providers/StoreUserContextProvider';
import { Context as HelperContext } from '../../../providers/HelperContextProvider';
import { useMediaQueries } from '../../../helpers/hooks';
import AppText from '../../common/AppText';
import WsProductCard from './webstore/sections/productGrid/WsProductCard';
import AppFlexbox from '../../common/AppFlexbox';
import AppTitle from '../../common/AppTitle';
import { PLATFORM_PK_SERVICE } from '../../../config/constants';

const MAX_PRODUCTS_DISPLAYED = 12;
const MOBILE_COLUMN_COUNT = 1;
const LAPTOP_COLUMN_COUNT = 2;
const DESKTOP_COLUMN_COUNT = 4;

const EcomProductCollectionShowcase = ({
  pkEcomStore,
  pkRegAssociation,
  loading,
  analyticCta,
  includeUrgencyWarning,
  extraPadding,
  hideNoMerch,
  showCollectionTitle,
  titleOverride,
  excludedProductUrlHandles,
  onNavigate
}) => {
  const fetchedPkStore = useRef(null);
  const { isTabletOrSmaller, isDesktopOrSmaller } = useMediaQueries();
  const { state, fetchEcomStoreCollectionsHistory } = useContext(
    StoreUserContext
  );
  const { toggleWebstoreProductsShowcaseModal } = useContext(HelperContext);
  const ecomStoreCollection = state.ecomStoreCollectionHistory.value.find(
    (c) => c.fkEcomStore === pkEcomStore
  );
  const vendorProducts =
    ecomStoreCollection?.ecomStoreCollectionProducts
      .map((p) => p.ecomStoreProduct)
      .filter(
        (p) =>
          !excludedProductUrlHandles ||
          !excludedProductUrlHandles.includes(p.urlHandle)
      )
      .sort((a, b) => a.sort - b.sort) ?? [];

  const showCaseLoading =
    pkEcomStore &&
    (state.ecomStoreCollectionHistory.loading ||
      loading ||
      (!ecomStoreCollection && fetchedPkStore.current !== pkEcomStore));

  const colorScheme = {
    textColor: '#000',
    outlineButtonColor: '#000'
  };

  const columnCount = isTabletOrSmaller
    ? MOBILE_COLUMN_COUNT
    : isDesktopOrSmaller
    ? LAPTOP_COLUMN_COUNT
    : DESKTOP_COLUMN_COUNT;

  const carouselEnabled =
    !showCaseLoading &&
    ecomStoreCollection &&
    ecomStoreCollection?.ecomStoreCollectionProducts.length > columnCount;

  useEffect(() => {
    if (pkEcomStore) {
      if (!ecomStoreCollection) {
        fetchEcomStoreCollectionsHistory(pkEcomStore, {
          pkServices: [PLATFORM_PK_SERVICE]
        });
      }

      fetchedPkStore.current = pkEcomStore;
    }
  }, [pkEcomStore]);

  return (
    (!hideNoMerch || vendorProducts.length > 0) && (
      <AppStack style={{ width: '100%' }}>
        {includeUrgencyWarning && (
          <>
            <Divider hiddenFrom="xs" style={{ marginTop: 20 }} />

            <Divider
              label="DON'T FORGET YOUR TEAM MERCH"
              labelPosition="center"
              style={{ marginTop: 20, marginBottom: 20 }}
              styles={{
                label: {
                  fontSize: 26,
                  fontWeight: 700,
                  whiteSpace: 'pre-wrap',
                  textAlign: 'center'
                }
              }}
            />

            <Divider hiddenFrom="xs" style={{ marginBottom: 20 }} />
          </>
        )}

        {pkEcomStore && (titleOverride || showCollectionTitle) && (
          <AppFlexbox>
            {showCaseLoading && !titleOverride ? (
              <Skeleton height={44} width="40%" />
            ) : (
              <AppTitle>{titleOverride || ecomStoreCollection?.name}</AppTitle>
            )}
          </AppFlexbox>
        )}

        {showCaseLoading || vendorProducts.length > 0 ? (
          <Carousel
            align={isTabletOrSmaller ? 'center' : 'start'}
            controlSize={40}
            draggable={carouselEnabled}
            loop={false}
            slideGap={16}
            slideSize={`${(isTabletOrSmaller ? 85 : 100) / columnCount}%`}
            slidesToScroll={columnCount}
            withControls={carouselEnabled}
            withIndicators={false}
          >
            {showCaseLoading ? (
              <>
                {Array.from(Array(columnCount)).map((x, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Carousel.Slide key={i}>
                    <WsProductCard
                      analyticCta={analyticCta}
                      colorScheme={colorScheme}
                      isQuickAddEnabled
                      onNavigate={() => {}}
                      showSecondImageOnHover
                    />
                  </Carousel.Slide>
                ))}
                <Carousel.Slide>
                  <WsProductCard colorScheme={colorScheme} />
                </Carousel.Slide>
                <Carousel.Slide>
                  <WsProductCard colorScheme={colorScheme} />
                </Carousel.Slide>
                <Carousel.Slide>
                  <WsProductCard colorScheme={colorScheme} />
                </Carousel.Slide>
              </>
            ) : (
              vendorProducts.slice(0, MAX_PRODUCTS_DISPLAYED).map((p) => (
                <Carousel.Slide key={p.pkEcomStoreProduct}>
                  <WsProductCard
                    analyticCta={analyticCta}
                    colorScheme={colorScheme}
                    ecomStoreProduct={p}
                    isQuickAddEnabled
                    onNavigate={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      toggleWebstoreProductsShowcaseModal({
                        isOpen: true,
                        ecomStoreProduct: p,
                        analyticCta
                      });
                      if (onNavigate) {
                        onNavigate(p);
                      }
                    }}
                    showSecondImageOnHover
                    url={`/association/${pkRegAssociation}?urlHandle=${p.urlHandle}`}
                  />
                </Carousel.Slide>
              ))
            )}
          </Carousel>
        ) : (
          <AppStack style={{ padding: 40 }}>
            <AppText
              style={{ textAlign: 'center', fontSize: 22, color: 'grey' }}
              weight={500}
            >
              No Merchandise Available. Please check back soon!
            </AppText>
          </AppStack>
        )}
      </AppStack>
    )
  );
};

EcomProductCollectionShowcase.propTypes = {
  analyticCta: PropTypes.string,
  excludePkEcomStoreProducts: PropTypes.array,
  excludedProductUrlHandles: PropTypes.array,
  extraPadding: PropTypes.bool,
  hideNoMerch: PropTypes.bool,
  includeUrgencyWarning: PropTypes.bool,
  loading: PropTypes.bool,
  onNavigate: PropTypes.func,
  pkEcomStore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pkRegAssociation: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showCollectionTitle: PropTypes.bool,
  titleOverride: PropTypes.string
};

export default EcomProductCollectionShowcase;
